(ns hai.spaces.data
  "Re-frame events for spaces: create, item link/unlink, rename, delete, and
   co-owner management. All go through the pathom `:resolve` effect and re-resolve
   the affected space (or navigate) on success.

   `defevent-fx` binds params after the cofx positionally to the event vector's
   tail (see `hai.ui.rf-sugar`), so a handler for `[::rename uuid title]` is
   `[_ uuid title]` — not `[_ [_ uuid title]]`."
  (:require
   [hai.ui.rf-sugar :refer [defevent defevent-fx defsub]]))

;; Spaces are admin-only: note and task tag fields only offer them to admins
;; (matching the sidebar's Spaces nav gate).
(defsub ::enabled?
  [admin? :<- [:auth/admin?]]
  admin?)

(defn- resolve-space-fx
  [space-uuid]
  [[:dispatch [:entity/resolve {:space/uuid space-uuid}]]])

;; The two shared-with tables on the detail page — People (the space) and
;; Co-owners (the hidden space-mgmt) — are both `sharing.components/permissions-table`
;; collections, addressed by that component's id: `[::permissions-table ident]`.
;; The `space/add-co-owner` mutation shares BOTH entities, so a successful add
;; must re-resolve both tables (their own share forms would, but this add goes
;; through the dedicated button).
(defn- permissions-table-resolve-fx
  [ident]
  [:dispatch [:table/resolve [:hai.sharing.components/permissions-table ident]]])

;; The space never exposes its space-mgmt in its own shape, so the co-owners
;; table needs the mgmt uuid resolved on its own; kept under `::mgmt-uuid`,
;; keyed by space uuid.
(defsub ::mgmt-uuid
  [db space-uuid]
  (get-in db [::mgmt-uuid (uuid (str space-uuid))]))

(defevent-fx ::load-mgmt
  [_ space-uuid]
  (let [su (uuid (str space-uuid))]
    {:resolve {:query        [{[:space/uuid su] [:space/mgmt-uuid]}]
               :transform-fn (fn [res] (-> res vals first :space/mgmt-uuid))
               :store-in     [::mgmt-uuid su]}}))

(defevent-fx ::create
  [{:keys [db]} title]
  (let [space-uuid (random-uuid)]
    ;; Flag the just-created space so its detail page focuses the title box
    ;; (the People share input auto-focuses otherwise). Cleared once consumed.
    {:db (assoc db ::just-created space-uuid)
     :resolve {:query [(list 'space/create {:space/uuid space-uuid
                                            :space/title (or title "New space")})]
               :on-success-fx [[:dispatch [:routing/navigate
                                           [:spaces/detail {:space/uuid space-uuid}]]]]}}))

;; Whether this space was just created in this session (drives the one-shot
;; title-box focus on its detail page).
(defsub ::just-created?
  [db space-uuid]
  (= (::just-created db) (uuid (str space-uuid))))

(defevent ::clear-just-created
  [db _]
  (dissoc db ::just-created))

(defevent-fx ::rename
  [_ space-uuid title]
  {:resolve {:query [(list 'space/update {:space/uuid space-uuid :space/title title})]
             :on-success-fx (resolve-space-fx space-uuid)}})

(defevent-fx ::update-description
  [_ space-uuid description]
  {:resolve {:query [(list 'space/update {:space/uuid space-uuid :space/description description})]
             :on-success-fx (resolve-space-fx space-uuid)}})

(defevent-fx ::add-item
  ;; `item` is a single-key map: {:note/uuid ...} or {:task/uuid ...}
  [_ space-uuid item]
  {:resolve {:query [(list 'space/add-item (merge {:space/uuid space-uuid} item))]
             :on-success-fx (resolve-space-fx space-uuid)}})

(defevent-fx ::remove-item
  [_ space-uuid item]
  {:resolve {:query [(list 'space/remove-item (merge {:space/uuid space-uuid} item))]
             :on-success-fx (resolve-space-fx space-uuid)}})

(defevent-fx ::add-co-owner
  ;; `share` is a scope map (`sharing.components/->share`): a picked contact
  ;; ({:contact/uuid …}) or a typed email ({:contact/email […]}).
  [_ space-uuid mgmt-uuid share]
  {:resolve {:query [(list 'space/add-co-owner {:space/uuid space-uuid
                                                :share share})]
             ;; The add shares both entities, so refresh both tables: People
             ;; (the space gained an admin) and Co-owners (the space-mgmt gained
             ;; a member).
             :on-success-fx [(permissions-table-resolve-fx [:space/uuid (uuid (str space-uuid))])
                             (permissions-table-resolve-fx [:space-mgmt/uuid (uuid (str mgmt-uuid))])]}})

(defevent-fx ::delete
  [_ space-uuid]
  ;; Delete happens from the spaces list itself, so re-resolve that table (the
  ;; `::table` collection in `hai.spaces.table`) to drop the deleted row —
  ;; navigating to the same page wouldn't re-run its query.
  {:resolve {:query [(list 'space/delete {:space/uuid space-uuid})]
             :on-success-fx [[:dispatch [:table/resolve :hai.spaces.table/table]]]}})
